/**
 * Cache em memória das liberações (pós-POST e pré-carga STATISTICS).
 * validUntil = extension valueDate; recorded = emissão; status cancelled = revogada.
 * @module purchase-release/releaseMemory
 */

import type { MedicationDispense } from "fhir/r5";
import dayjs from "dayjs";

import {
  getDispenseBatchId,
  getDispenseRecordedAt,
  getDispenseValidUntil,
  isActivePurchaseDispense,
} from "./listHelpers";
import type { LocalPurchaseReleaseBatch } from "./types";

/** Mesmo Map usado por useLocalPurchaseReleases. */
export const purchaseReleaseMemory = new Map<
  string,
  LocalPurchaseReleaseBatch[]
>();

/** Listeners para UI reagir à pré-carga / POST / revogação. */
const memoryListeners = new Set<() => void>();

export function subscribePurchaseReleaseMemory(
  listener: () => void,
): () => void {
  memoryListeners.add(listener);
  return () => {
    memoryListeners.delete(listener);
  };
}

function notifyPurchaseReleaseMemory(): void {
  memoryListeners.forEach((listener) => listener());
}

export function setPatientPurchaseReleaseBatches(
  patientKey: string,
  batches: LocalPurchaseReleaseBatch[],
): void {
  const key = patientKey.trim();
  if (!key) return;
  purchaseReleaseMemory.set(key, batches);
  notifyPurchaseReleaseMemory();
}

export type PatientReleaseListStatus =
  | "none"
  | "expired"
  | "valid"
  | "mixed";

export interface PatientReleaseSummary {
  status: PatientReleaseListStatus;
  hasAny: boolean;
  hasExpired: boolean;
  hasValid: boolean;
}

function activeBatches(
  batches: LocalPurchaseReleaseBatch[],
): LocalPurchaseReleaseBatch[] {
  return batches
    .map((batch) => ({
      ...batch,
      dispenses: batch.dispenses.filter(isActivePurchaseDispense),
    }))
    .filter((batch) => batch.dispenses.length > 0);
}

function batchIsExpired(
  batch: LocalPurchaseReleaseBatch,
  todayYmd: string,
): boolean {
  const dates = batch.dispenses
    .map(getDispenseValidUntil)
    .filter((d): d is string => Boolean(d));
  if (!dates.length) return false;
  const min = [...dates].sort()[0];
  return dayjs(min).isBefore(dayjs(todayYmd), "day");
}

function batchIsValid(
  batch: LocalPurchaseReleaseBatch,
  todayYmd: string,
): boolean {
  const dates = batch.dispenses
    .map(getDispenseValidUntil)
    .filter((d): d is string => Boolean(d));
  if (!dates.length) return true;
  const min = [...dates].sort()[0];
  return !dayjs(min).isBefore(dayjs(todayYmd), "day");
}

export function peekPatientReleaseSummary(
  patientKey: string,
): PatientReleaseSummary {
  const batches = activeBatches(
    purchaseReleaseMemory.get(patientKey.trim()) ?? [],
  );
  const today = dayjs().format("YYYY-MM-DD");

  if (!batches.length) {
    return {
      status: "none",
      hasAny: false,
      hasExpired: false,
      hasValid: false,
    };
  }

  const hasExpired = batches.some((b) => batchIsExpired(b, today));
  const hasValid = batches.some((b) => batchIsValid(b, today));

  let status: PatientReleaseListStatus = "mixed";
  if (hasExpired && !hasValid) status = "expired";
  else if (hasValid && !hasExpired) status = "valid";
  else if (hasValid && hasExpired) status = "mixed";
  else status = "valid";

  return { status, hasAny: true, hasExpired, hasValid };
}

/**
 * Agrupa MedicationDispense FHIR (STATISTICS) em lotes de cache UI.
 * Ignora status=cancelled. validUntil = extension; createdAt prefer recorded.
 */
export function groupDispensesIntoBatches(
  dispenses: MedicationDispense[],
  patientKey: string,
  organizationId: string,
  storeName = "",
): LocalPurchaseReleaseBatch[] {
  const groups = new Map<string, MedicationDispense[]>();

  for (const dispense of dispenses) {
    if (!isActivePurchaseDispense(dispense)) continue;
    const batchId =
      getDispenseBatchId(dispense) ||
      `single-${dispense.id || Math.random().toString(36).slice(2)}`;
    const list = groups.get(batchId) ?? [];
    list.push(dispense);
    groups.set(batchId, list);
  }

  const batches: LocalPurchaseReleaseBatch[] = [];
  for (const [batchId, items] of groups) {
    const recordedTimes = items
      .map(getDispenseRecordedAt)
      .filter((d): d is string => Boolean(d))
      .sort();
    batches.push({
      batchId,
      patientKey,
      organizationId,
      storeId: organizationId,
      storeName,
      createdAt: recordedTimes[recordedTimes.length - 1] || dayjs().toISOString(),
      dispenses: items,
    });
  }

  return batches.sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
  );
}

/**
 * Hidrata o Map a partir da pré-carga STATISTICS (merge por batchId).
 */
export function hydratePurchaseReleasesFromDispenses(
  dispensesByPatient: Map<string, MedicationDispense[]>,
  organizationId: string,
  storeName = "",
): void {
  const orgId = organizationId.trim();
  if (!orgId) return;

  for (const [patientKey, dispenses] of dispensesByPatient) {
    const key = patientKey.trim();
    if (!key) continue;

    const fromServer = groupDispensesIntoBatches(
      dispenses,
      key,
      orgId,
      storeName,
    );
    const existing = purchaseReleaseMemory.get(key) ?? [];
    const byId = new Map(fromServer.map((b) => [b.batchId, b]));

    // Mantém lotes locais ainda não presentes na resposta (ex.: POST recente)
    for (const local of existing) {
      if (!byId.has(local.batchId)) {
        byId.set(local.batchId, local);
      }
    }

    purchaseReleaseMemory.set(key, [...byId.values()]);
  }

  notifyPurchaseReleaseMemory();
}
